// ─────────────────────────────────────────────
//  canonicalPerson — one human, however the sheet spelled them.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  The daily sheet is typed by colleagues who know each other, so it says
//  "Josh Larson", "Jim Slone", "Bob Schneider". The directory holds the name
//  payroll knows: Joshua Larson, James Slone, Robert Schneider. Matching on the
//  full string missed every one of them, which is why the importer kept
//  concluding these were new people.
//
//  ⚠️ SURNAME ALONE IS NOT AN IDENTITY, AND THIS IS THE WHOLE POINT.
//  Measured in the live directory on 2026-08-20:
//      Bennett → Casey (CRNA) · Elizabeth (CRNA) · Dr. Amanda
//      John    → Oluwafemi     · Smitha
//      Sanchez → Charlotte     · Alfredo
//  A matcher that fell back to the surname would have attached "Bennett" to
//  whichever of three colleagues happened to sort first. In a system that
//  decides who is in a room with a patient, a confident wrong answer is worse
//  than no answer — so an ambiguous surname resolves to NOTHING and the name is
//  reported as unmatched for a human to settle.
//
//  Pure: no Firebase, no React. Takes a name and a list of candidate people.
// ─────────────────────────────────────────────

// Familiar forms seen in this department's sheet, mapped to the formal first
// name the directory uses. Deliberately a SHORT, evidenced list rather than a
// general nickname dictionary: every entry below was confirmed against a real
// pair of records, and a guess here silently reassigns a person's cases.
const FIRST_NAME_ALIASES = new Map(Object.entries({
  josh: "joshua",
  jim: "james",
  bob: "robert",
  gabi: "gabriela",
  femi: "oluwafemi",
  // Common enough to be safe, and cheap to carry:
  mike: "michael",
  matt: "matthew",
  dave: "david",
  dan: "daniel",
  tom: "thomas",
  chris: "christopher",
  nick: "nicholas",
  tony: "anthony",
  toni: "antonia",
  liz: "elizabeth",
  beth: "elizabeth",
  cathy: "catherine",
  kathy: "katherine",
  steve: "stephen",
  jeff: "jeffrey",
  greg: "gregory",
  rob: "robert",
  rick: "richard",
  dick: "richard",
  bill: "william",
  will: "william",
  ben: "benjamin",
  sam: "samuel",
  alex: "alexander",
  andy: "andrew",
  charlie: "charles",
  jen: "jennifer",
  jenny: "jennifer",
  sue: "susan",
  pam: "pamela",
  pat: "patricia",
  deb: "deborah",
}));

const letters = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");

/** "Dr. Amanda Bennett" → ["amanda","bennett"]; titles and initials dropped. */
export function nameParts(name) {
  return String(name || "")
    .replace(/\b(dr|mr|mrs|ms|md|do|crna|aa|rn|srna)\b\.?/gi, " ")
    .split(/\s+/)
    .map(letters)
    .filter((p) => p.length > 1); // drops middle initials
}

/** The formal form of a first name, when we know one. */
export const canonicalFirst = (first) => FIRST_NAME_ALIASES.get(letters(first)) || letters(first);

/** Surname, or "" when the name has only one part. */
export function surnameOf(name) {
  const p = nameParts(name);
  return p.length > 1 ? p[p.length - 1] : "";
}

/**
 * Does `sheetName` name the same human as `candidateName`?
 *
 * True when the surnames match AND the first names match after alias folding
 * (in either direction — the sheet may carry the formal name and the directory
 * the familiar one).
 *
 * ⚠️ NO FIRST-INITIAL MATCHING. An earlier draft accepted "J. Larson" as
 * Joshua. Two reasons it is gone: nothing in the live sheet writes initials, so
 * it was unevidenced behaviour; and with three Bennetts and two Johns on file,
 * a single letter is the least safe thing to match on. A name that only carries
 * an initial now resolves to nobody and gets reported, which is the honest
 * answer.
 */
export function sameHuman(sheetName, candidateName) {
  const a = nameParts(sheetName);
  const b = nameParts(candidateName);
  if (a.length < 2 || b.length < 2) return false;
  if (a[a.length - 1] !== b[b.length - 1]) return false;
  return canonicalFirst(a[0]) === canonicalFirst(b[0]);
}

/**
 * Resolve a sheet name to exactly one person, or to nothing.
 *
 * @param {string} sheetName            the cell's name, already cleaned
 * @param {Array<{name:string}>} people candidate records (staff or users)
 * @returns {{ match: object|null, reason: "exact"|"alias"|"ambiguous"|"none",
 *             candidates: object[] }}
 *
 * ⚠️ AMBIGUOUS RESOLVES TO NULL. Two people sharing a surname, with nothing in
 * the cell to tell them apart, is not a match — it is a question. Returning one
 * of them would put the wrong colleague in a room.
 */
export function resolvePerson(sheetName, people = []) {
  const clean = String(sheetName || "").trim();
  if (!clean) return { match: null, reason: "none", candidates: [] };
  const list = Array.isArray(people) ? people.filter(Boolean) : [];

  // An exact (letters-only) hit always wins, even when a surname is shared —
  // "Casey Bennett" is not ambiguous just because Elizabeth exists.
  const exact = list.filter((p) => letters(p.name) === letters(clean));
  if (exact.length === 1) return { match: exact[0], reason: "exact", candidates: exact };
  if (exact.length > 1) return { match: null, reason: "ambiguous", candidates: exact };

  const alias = list.filter((p) => sameHuman(clean, p.name));
  if (alias.length === 1) return { match: alias[0], reason: "alias", candidates: alias };
  if (alias.length > 1) return { match: null, reason: "ambiguous", candidates: alias };

  return { match: null, reason: "none", candidates: [] };
}
